import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, parseISO, format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import openSocket from "../../services/socket-io";
import {
  Clock,
  Check,
  CheckCheck,
  ChevronDown,
  Ban,
  Download,
  Loader2,
  StickyNote,
  FileText,
} from "lucide-react";

import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
      return [...state];
    }

    // Entra pela data, não no fim da lista. O socket não garante ordem de
    // chegada (mídia baixada demora mais que texto, e o eco do WhatsApp pode
    // vir depois do que já foi gravado), e empurrar sempre para o fim deixava
    // a conversa fora de ordem até dar F5.
    const nova = [...state, newMessage];
    nova.sort((a, b) => {
      const diff = new Date(a.createdAt) - new Date(b.createdAt);
      return diff !== 0 ? diff : String(a.id).localeCompare(String(b.id));
    });

    return nova;
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "SET_REACTION") {
    if (action.payload.mapa) {
      const mapa = action.payload.mapa;
      return state.map((msg) => ({
        ...msg,
        reacoes: mapa[msg.id] || msg.reacoes || [],
      }));
    }
    if (action.payload.messageId) {
      return state.map((msg) => {
        if (msg.id === action.payload.messageId) {
          return { ...msg, reacoes: action.payload.reacoes };
        }
        return msg;
      });
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup }) => {
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ behavior: "instant" });
      }
      const container = document.getElementById("messagesList");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 20);
  };

  useEffect(() => {
    setLoading(true);
    const fetchMessages = async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber },
        });

        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          if (data.reactions) {
            dispatch({
              type: "SET_REACTION",
              payload: { mapa: data.reactions },
            });
          }
          setHasMore(data.hasMore);
          setLoading(false);

          if (pageNumber === 1) {
            scrollToBottom();
          }
        }
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };

    fetchMessages();
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (Number(data.message?.ticketId) !== Number(ticketId)) return;

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    socket.on("appMessageReaction", (data) => {
      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const extrairNomeArquivo = (mediaUrl) => {
    if (!mediaUrl) return "Documento";
    try {
      const decoded = decodeURIComponent(mediaUrl.split("/").pop() || "");
      const semQuery = decoded.split("?")[0];
      let nomeLimpo = semQuery.replace(/^\.+/, "");
      const partes = nomeLimpo.split(".");
      if (partes.length >= 3) {
        const idCandidato = partes[partes.length - 2];
        if (idCandidato && idCandidato.length === 5 && /^[a-zA-Z0-9]+$/.test(idCandidato)) {
          partes.splice(partes.length - 2, 1);
          nomeLimpo = partes.join(".");
        }
      } else if (partes.length === 2 && /^[a-zA-Z0-9]{4,8}$/.test(partes[0])) {
        nomeLimpo = partes[1];
      }
      return nomeLimpo || "Documento";
    } catch {
      return "Documento";
    }
  };

  // Numa mensagem de mídia o corpo é a legenda. Mensagens antigas guardavam o
  // nome do arquivo ali — nesse caso não há legenda de verdade para mostrar.
  const corpoVisivel = (message) => {
    if (!message.mediaUrl || !message.body) return message.body;

    const arquivo = decodeURIComponent(message.mediaUrl.split("/").pop() || "");

    return message.body.trim() === arquivo ? "" : message.body;
  };

  const checkMessageMedia = (message) => {
    if (
      message.mediaType === "location" &&
      message.body.split("|").length >= 2
    ) {
      let locationParts = message.body.split("|");
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];

      let descriptionLocation = null;

      if (locationParts.length > 2)
        descriptionLocation = message.body.split("|")[2];

      return (
        <LocationPreview
          image={imageLocation}
          link={linkLocation}
          description={descriptionLocation}
        />
      );
    } else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />;
    } else if (message.mediaType === "image") {
      // Antes havia também uma checagem de extensão (jpe?g|png|gif). Ela era
      // redundante — o backend já deriva mediaType do mimetype — e deixava de
      // fora tudo o mais que o WhatsApp manda: .webp (o formato padrão de
      // figurinha e de foto encaminhada), .heic do iPhone, .bmp, .avif. O que
      // não casava virava botão de download, e a conversa ficava com um
      // "Download" no lugar da imagem.
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />;
    } else if (message.mediaType === "video") {
      return (
        <video
          className="h-[200px] w-[250px] rounded-lg object-cover"
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      const nomeArquivo = extrairNomeArquivo(message.mediaUrl);
      return (
        <div className="flex items-center gap-3 border-b p-2.5 max-w-[280px]">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black/5 text-foreground/70 dark:bg-white/10 dark:text-foreground/90">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-xs font-medium text-foreground"
              title={nomeArquivo}
            >
              {nomeArquivo}
            </p>
            <a
              href={message.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={nomeArquivo}
              className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
            >
              <Download className="h-3 w-3" />
              Download
            </a>
          </div>
        </div>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <Clock className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 1) {
      return <Check className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 2) {
      return <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle text-emerald-500" />
      );
    }
  };

  // "Hoje" e "Ontem" poupam quem lê de converter a data mentalmente; só a
  // partir de anteontem o número passa a ser mais informativo que o rótulo.
  const rotuloDoDia = (data) => {
    const dia = parseISO(data);

    if (isSameDay(dia, new Date())) return "Hoje";
    if (isSameDay(dia, subDays(new Date(), 1))) return "Ontem";

    return format(dia, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <div
          key={`timestamp-${message.id}`}
          className="sticky top-1 z-10 mx-auto my-3 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
        >
          {rotuloDoDia(messagesList[index].createdAt)}
        </div>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <div
            key={`timestamp-${message.id}`}
            className="sticky top-1 z-10 mx-auto my-3 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
          >
            {rotuloDoDia(messagesList[index].createdAt)}
          </div>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return <span className="mt-4" key={`divider-${message.id}`}></span>;
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={cn(
          "-mx-1.5 -my-0.5 mb-1.5 flex overflow-hidden rounded-lg",
          message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "w-1 shrink-0",
            message.quotedMsg?.fromMe ? "bg-emerald-400" : "bg-sky-400",
          )}
        />
        <div className="min-w-0 overflow-hidden whitespace-pre-wrap p-2.5 text-sm">
          {!message.quotedMsg?.fromMe && (
            <span className="flex font-medium text-sky-500">
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  // O canto reto de um lado só é o que dá a "ponta" da bolha e diz de quem
  // é a fala sem precisar de cor forte. Sombra fica quase imperceptível: com
  // centenas de bolhas na tela, sombra pesada vira ruído.
  const bubbleBase =
    "relative mt-0.5 block h-auto max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-2xl px-3 pb-1.5 pt-2 shadow-[0_1px_2px_rgba(0,0,0,0.06)] sm:max-w-[600px]";

  const reagir = async (message, emoji) => {
    try {
      await api.post(`/messages/${message.id}/reaction`, { emoji });
    } catch (err) {
      toastError(err);
    }
  };

  const renderReacoes = (message) => {
    if (!message.reacoes?.length) return null;

    return (
      <div
        className={cn(
          "absolute -bottom-3.5 z-10 flex items-center gap-1",
          message.fromMe ? "right-3" : "left-3"
        )}
      >
        {message.reacoes.map((reacao) => (
          <button
            key={reacao.emoji}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              reagir(message, reacao.emoji);
            }}
            title={
              reacao.euReagi ? "Remover sua reação" : "Reagir com este emoji"
            }
            aria-pressed={reacao.euReagi}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200/90 bg-white shadow-sm transition-transform hover:scale-125 dark:border-slate-700 dark:bg-slate-800"
          >
            <span className="text-sm leading-none">{reacao.emoji}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.isInternal) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div className="my-2 flex w-full justify-center px-4">
                <span className="flex items-center gap-1.5 rounded-full border border-dashed bg-muted/60 px-3 py-1 text-center text-xs text-muted-foreground">
                  <StickyNote className="h-3 w-3 shrink-0" />
                  {message.body}
                </span>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={cn(
                  bubbleBase,
                  "group mr-5 self-start rounded-bl-md bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-100",
                  message.reacoes?.length && "mb-3.5"
                )}
              >
                <button
                  type="button"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  className="absolute right-0 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {isGroup && (
                  <span className="flex font-medium text-sky-500">
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  checkMessageMedia(message)}
                <div className="wrap-break-word pb-1 pr-16 pt-0.5">
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{corpoVisivel(message)}</MarkdownWrapper>
                  <span className="absolute bottom-0.5 right-1.5 text-[11px] text-muted-foreground">
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
                {renderReacoes(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={cn(
                  bubbleBase,
                  "group ml-5 self-end rounded-br-md bg-[#dcf8c6] text-slate-800 dark:bg-emerald-900/45 dark:text-slate-100",
                  message.reacoes?.length && "mb-3.5"
                )}
              >
                <button
                  type="button"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  className="absolute right-0 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  checkMessageMedia(message)}
                <div
                  className={cn(
                    "wrap-break-word pb-1 pr-16 pt-0.5",
                    message.isDeleted && "italic text-muted-foreground",
                  )}
                >
                  {message.isDeleted && (
                    <Ban className="mr-1 inline h-3.5 w-3.5 align-middle" />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{corpoVisivel(message)}</MarkdownWrapper>
                  <span className="absolute bottom-0.5 right-1.5 flex items-center text-[11px] text-muted-foreground">
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
                {renderReacoes(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      {/* A textura é clara; no tema escuro ela entra esmaecida sobre o fundo
          do tema para as bolhas não perderem contraste. */}
      <div
        id="messagesList"
        onScroll={handleScroll}
        className="scrollbar-discreta relative flex flex-1 flex-col overflow-y-auto bg-muted/40"
      >
        {/* Este wrapper existe para a textura ter em que se ancorar.
            Ela era `absolute inset-0` direto no contêiner de rolagem, e
            elemento absoluto ali é dimensionado pela área VISÍVEL, não pela
            altura do conteúdo: passando de uma tela de mensagens, o resto
            rolava para fora da textura e o fundo ficava branco.
            Aqui a altura acompanha as mensagens, e o min-h-full garante a
            textura inteira também quando há pouca conversa. O shrink-0 é
            obrigatório: o pai é flex-col, e item flex encolhe para caber em
            vez de crescer — sem ele o wrapper voltava a ter a altura da
            janela e o problema continuava. */}
        <div className="relative flex min-h-full shrink-0 flex-col p-3 pb-5 sm:p-5">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-repeat opacity-100 dark:opacity-[0.06]"
            style={{ backgroundImage: `url(${whatsBackground})` }}
          />
          <div className="relative flex flex-1 flex-col">
            {messagesList.length > 0 ? renderMessages() : []}
          </div>
        </div>
      </div>
      {loading && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500 opacity-70" />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
